import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import {
    BehaviorSubject,
    forkJoin,
    Observable,
    Subject,
    throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { USER_GEO_LEVEL } from '../../_constants/appSetting.constant';
import { LOCALSTORAGEKEYS } from '../../_constants/localStorageKeys';
import { roles } from '../../_constants/roles';
import { STATES } from '../../_constants/states';
import { ImageTypeEnum } from '../../_enum/common.enum';
import { HttpHeaderConfigInterceptor } from '../../_interceptor/http-header-config.interceptor';
import { CdnService } from '../_cdn/cdn.service';
import { LocalService } from '../_storage/local.service';
import { SERVICE_URL } from '../../_urls/service.urls';

@Injectable({
    providedIn: 'root',
})
export class CommonService {
    cdnService = inject(CdnService);
    cacheKeycloakUrl = `${environment.keyClockApiBaseUrl}/${SERVICE_URL.KEYCLOAK_REALMS}${environment.keycloak.realm}/`;
    imageBaseUrl = environment.apiBaseUrl + SERVICE_URL.VAULT_PROFILE;
    customColumnEventListner$ = new Subject<any>();
    customActionEventListner$ = new Subject<any>();
    pageRefreshEventListner$ = new Subject<any>();
    stateList = STATES;

    tableRefreshSubject = new BehaviorSubject<boolean>(false);
    isTableRefresh = this.tableRefreshSubject.asObservable();

    constructor(
        private router: Router,
        private localService: LocalService,
        protected _keycloakAngular: KeycloakService,
        private http: HttpClient
    ) {}

    /**
     * Prepare financial year
     * @returns array of financial year
     */
    prepareFinancialYear(tillYears = 15) {
        let currentYear: any;
        const year = new Date().getFullYear();
        const month = new Date().getMonth() + 1;

        // ! Financial year should change only in march and not before that
        if (month >= 4) {
            currentYear = `${year}`;
        } else {
            currentYear = `${year - 1}`;
        }
        const financialYear = [];
        for (let i = 0; i < tillYears; i++) {
            financialYear.push({
                label: `${currentYear - i} - ${currentYear - i + 1}`,
                value: `${currentYear - i}-${currentYear - i + 1}`,
            });
        }
        return financialYear;
    }

    // This function is used to prepare dynamic financial year
    prepareDynamicYear(tillYears = 15, page = 0) {
        let currentYear: any;
        const year = new Date().getFullYear() + page * 3;
        const month = new Date().getMonth() + 1;

        // ! Financial year should change only in march and not before that
        if (month >= 4) {
            currentYear = `${year}`;
        } else {
            currentYear = `${year - 1}`;
        }
        const financialYear = [];
        for (let i = 0; i < tillYears; i++) {
            financialYear.push({
                label: `${currentYear - i} - ${currentYear - i + 1}`,
                value: `${currentYear - i}-${currentYear - i + 1}`,
            });
        }
        return financialYear;
    }
    /**
     * Get State Sort Name
     * @param stateId
     * @returns
     */
    getStateSortName(stateId: string | number) {
        const state = this.stateList.find((s: any) => {
            const sid = s.state_id.toString();
            const formattedId =
                sid.length === 1 &&
                stateId.toString().length === 2 &&
                stateId.toString().startsWith('0')
                    ? '0' + sid
                    : sid;
            return formattedId === stateId.toString();
        });
        return state?.state_short_name_en?.toLowerCase() || '';
    }

    /**
     * check roles
     * @param role
     * @returns
     */
    checkRoles(role: string) {
        const userRoles =
            this.localService.get(LOCALSTORAGEKEYS.USERROLES) || [];
        const isRoleFound = userRoles.includes(role);
        if (isRoleFound) {
            return role;
        } else {
            this.router.navigate(['/main/unauthorised-access']);
            return '';
        }
    }

    getDecodecRole(role: string) {
        let decodedRole = '';
        try {
            decodedRole = atob(role);
        } catch (error) {
            console.error(error);
            this.router.navigate(['/main/unauthorised-access']);
        }
        return decodedRole;
    }

    getLocationArray(rolesL: string[]) {
        const sortOrder = ['National', 'State', 'District', 'Block'];
        const userRoles =
            this.localService.get(LOCALSTORAGEKEYS.USERROLES) || [];

        const result = rolesL.reduce(
            (acc, fullRole) => {
                if (!userRoles.includes(fullRole)) {
                    return acc;
                }

                const parts = fullRole.split('-');
                const locationRaw = parts[0];
                let location =
                    locationRaw.charAt(0).toUpperCase() +
                    locationRaw.slice(1).toLowerCase();

                if (roles.CLF_BK === fullRole) {
                    location = 'Block';
                }

                const existing = acc.find((item) => item.location === location);
                if (existing) {
                    existing.roles.push(fullRole);
                } else {
                    acc.push({ location, roles: [fullRole] });
                }

                return acc;
            },
            [] as { location: string; roles: string[] }[]
        );

        result.sort((a, b) => {
            return (
                sortOrder.indexOf(a.location) - sortOrder.indexOf(b.location)
            );
        });

        return result;
    }
    /**
     * get user geo location
     * @param role
     * @param geoLocation
     */
    getUserGeoLocation(role: string, geoLocation: string) {
        switch (geoLocation) {
            case 'national':
                const nationalStateList =
                    this.localService.get('nationalPermissions') || [];
                return nationalStateList;
            case 'state':
                const stateList =
                    this.localService.get('statePermissions') || [];
                return stateList.filter((state: any) => state.role === role);
            case 'district':
                const districtList =
                    this.localService.get('districtPermissions') || [];
                return districtList.filter(
                    (district: any) => district.role === role
                );
            case 'block':
                const blockList =
                    this.localService.get('blockPermissions') || [];
                return blockList.filter((block: any) => block.role === role);
            default:
                return [];
        }
    }

    setLocationId(locationId: string | number, type: string) {
        switch (type) {
            case 'state':
                HttpHeaderConfigInterceptor.locationId = `S-${locationId}`;
                break;
            case 'district':
                HttpHeaderConfigInterceptor.locationId = `D-${locationId}`;
                break;
            case 'block':
                HttpHeaderConfigInterceptor.locationId = `B-${locationId}`;
                break;
        }
    }

    /**
     * Format number as Indian currency string (e.g., 10,000.00)
     */
    formatIndianCurrency(value: number): string {
        return value != null
            ? value.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              })
            : '0.00';
    }

    currentMonth() {
        return new Date().toLocaleString('default', { month: 'long' });
    }

    yesterDayDate() {
        const currentDate = new Date();
        const yesterday = new Date(currentDate);
        yesterday.setDate(currentDate.getDate() - 1);
        const dd = String(yesterday.getDate()).padStart(2, '0');
        const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
        const yyyy = yesterday.getFullYear();

        return `${dd}-${mm}-${yyyy}`;
    }

    dayBeforeYesterdayDate() {
        const currentDate = new Date();
        const dayBeforeYesterday = new Date(currentDate);
        dayBeforeYesterday.setDate(currentDate.getDate() - 2);

        const dd = String(dayBeforeYesterday.getDate()).padStart(2, '0');
        const mm = String(dayBeforeYesterday.getMonth() + 1).padStart(2, '0');
        const yyyy = dayBeforeYesterday.getFullYear();

        return `${dd}-${mm}-${yyyy}`;
    }

    numberToYesNo(value: any) {
        return value ? 'YES' : 'NO';
    }

    getMonthAndYear(normalizedMonthAndYear: Date) {
        const month = normalizedMonthAndYear.getMonth() + 1;
        const year = normalizedMonthAndYear.getFullYear();

        return {
            year: year,
            month: month.toString().padStart(2, '0'),
            display: formatDate(normalizedMonthAndYear, 'MMMM-yyyy', 'en-US'),
        };
    }

    applyRoleBasedGeoIds(form: FormGroup, currentRole: any) {
        const stateList = this.getUserGeoLocation(currentRole, 'state')?.filter(
            (state: any) => !state.state_name_en.includes('STATE-')
        );
        const districtList = this.getUserGeoLocation(
            currentRole,
            'district'
        )?.filter(
            (district: any) => !district.district_name_en.includes('DISTRICT-')
        );
        const blockList = this.getUserGeoLocation(currentRole, 'block')?.filter(
            (block: any) => !block.block_name_en.includes('BLOCK-')
        );

        // Reset first
        form.patchValue(
            { stateId: '', districtId: '', blockId: '' },
            { emitEvent: false }
        );

        // NATIONAL ROLES
        if (
            [roles.NA, roles.NTSISD, roles.NMIB, roles.NRV].includes(
                currentRole
            )
        ) {
            const nationalStateList = this.getUserGeoLocation(
                currentRole,
                'national'
            )?.filter((state: any) => !state.state_name_en.includes('STATE-'));
            form.get('userGeoLevel')?.setValue(USER_GEO_LEVEL.STATE_LEVEL);
            return {
                isShowState: true,
                isShowDistrict: true,
                isShowBlock: true,
                stateList: nationalStateList,
                userGeoLevel: USER_GEO_LEVEL.STATE_LEVEL,
                isNationalUser: true,
            };
        }
        // STATE ROLES
        if (
            [roles.SMIB, roles.STSISD, roles.SA, roles.SRV].includes(
                currentRole
            )
        ) {
            const s = stateList[0];
            form.patchValue({ stateId: s?.state_id }, { emitEvent: false });
            form.get('stateId')?.valueChanges.subscribe((id) => {
                this.setLocationId(id, 'state');
                form.patchValue(
                    { districtId: '', blockId: '' },
                    { emitEvent: false }
                );
            });
            form.get('userGeoLevel')?.setValue(USER_GEO_LEVEL.STATE_LEVEL);
            return {
                isShowState: true,
                isShowDistrict: true,
                isShowBlock: true,
                stateList,
                userGeoLevel: USER_GEO_LEVEL.STATE_LEVEL,
            };
        }
        // DISTRICT ROLES
        if (
            [roles.DMIB, roles.DTSISD, roles.DTF, roles.DA, roles.DRV].includes(
                currentRole
            )
        ) {
            const d = districtList[0];
            form.patchValue(
                { stateId: d?.state_id, districtId: d?.district_id },
                { emitEvent: false }
            );
            form.get('districtId')?.valueChanges.subscribe((id) => {
                this.setLocationId(id, 'district');
                const selected = districtList.find(
                    (district: any) => district.district_id === id
                );
                if (!selected) return;
                form.patchValue(
                    { stateId: selected.state_id },
                    { emitEvent: false }
                );
            });
            form.get('userGeoLevel')?.setValue(USER_GEO_LEVEL.DISTRICT_LEVEL);
            return {
                isShowState: false,
                isShowDistrict: true,
                isShowBlock: true,
                districtList,
                userGeoLevel: USER_GEO_LEVEL.DISTRICT_LEVEL,
            };
        }
        // BLOCK ROLES
        if (
            [roles.BMIB, roles.BTF, roles.BA, roles.BRV].includes(currentRole)
        ) {
            const b = blockList[0];
            form.patchValue(
                {
                    stateId: b?.state_id,
                    districtId: b?.district_id,
                    blockId: b?.block_id,
                },
                { emitEvent: false }
            );
            form.get('blockId')?.valueChanges.subscribe((id) => {
                this.setLocationId(id, 'block');
                const selected = blockList.find(
                    (block: any) => block.block_id === id
                );
                if (!selected) return;
                form.patchValue(
                    {
                        stateId: selected.state_id,
                        districtId: selected.district_id,
                    },
                    { emitEvent: false }
                );
            });
            form.get('userGeoLevel')?.setValue(USER_GEO_LEVEL.BLOCK_LEVEL);
            return {
                isShowState: false,
                isShowDistrict: false,
                isShowBlock: true,
                blockList,
                userGeoLevel: USER_GEO_LEVEL.BLOCK_LEVEL,
            };
        }

        return null;
    }

    requiredNumberFormat(input: string | number): string {
        if (typeof input === 'number') {
            input = input.toString();
        }
        return input?.replace(/,/g, '') || '0';
    }

    convertDDMMYYYYFormat(input: number) {
        if (!input) return '-';

        let timestamp = input;
        const length = input.toString().length;

        if (length === 10) {
            timestamp = input * 1000;
        } else if (length === 13) {
            timestamp = input;
        } else if (length > 13) {
            timestamp = Math.floor(input / Math.pow(10, length - 13));
        }

        const date = new Date(timestamp);
        return date ? formatDate(date, 'dd-MM-yyyy', 'en-IN') : '-';
    }

    dateFunction(milliseconds: any): any {
        let ApproveDate: any = '';
        if (isNaN(milliseconds)) {
            ApproveDate = new Date(milliseconds).getTime();
            return ApproveDate;
        } else if (milliseconds) {
            const dataconvrt =
                milliseconds?.toString()?.length == 10
                    ? milliseconds * 1000
                    : milliseconds;
            const date = new Date(dataconvrt);
            return date?.toString() ? date?.toString() : '';
        }
    }

    formatDateTime(dateString: string) {
        // ✅ Handle null, undefined, empty string
        if (!dateString) {
            return '-';
        }

        const date = new Date(dateString);

        // ✅ Handle invalid date
        if (isNaN(date.getTime())) {
            return '-';
        }

        const day = date.getDate().toString().padStart(2, '0');

        const month = date.toLocaleString('en-US', {
            month: 'long',
        });

        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');

        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12 || 12;

        return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
    }

    addDaysAndFormat(
        date: Date | string | number,
        days: number,
        format = 'dd-MM-yyyy',
        locale = 'en-IN'
    ): string {
        if (!date) return '-';

        const d = new Date(date);
        d.setDate(d.getDate() + days);

        return formatDate(d, format, locale);
    }

    /**
     * Returns the minimum allowed meeting date for the very first CLF meeting.
     * Equals March 31 of the financial year that ended 2 FYs ago.
     * Indian FY: April 1 → March 31.
     * Example: In FY 2025-26 (start year 2025) → returns March 31, 2023.
     *          In FY 2026-27 (start year 2026) → returns March 31, 2024.
     */
    getClfMinStartDate(): Date {
        const today = new Date();
        const currentMonth = today.getMonth() + 1; // 1-12
        const currentYear = today.getFullYear();
        // FY start year: April onwards = current year; Jan–March = previous year
        const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
        // Last day of FY ending 2 years ago = March 31 of (fyStartYear - 1)
        return new Date(fyStartYear, 2, 1); // month index 2 = March
    }

    /**
     * Converts a date string in DD-MM-YYYY format to a timestamp (milliseconds since epoch)
     * @param dateStr string in DD-MM-YYYY format
     * @returns number timestamp
     */

    dateStringToTimestamp(dateStr: string): number {
        if (!dateStr) return 0;
        const [day, month, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return 0;
        return new Date(year, month - 1, day).getTime();
    }

    getSubPermissions(id: string) {
        const url = `${this.cacheKeycloakUrl}permission/${id}`;
        return this.http.get<string[]>(url, { observe: 'body' }).toPromise();
    }

    getClfListsById(
        id: string,
        cboUuid: string,
        singleCall: boolean,
        contentType?: 'bank' | 'basic' | 'member',
        cboType?: any
    ): Observable<any> {
        const params = { cbo_type: cboType };
        const basicParams = { fed_id: id };

        const bankUrl = `${environment.fdmApiBaseUrl}${SERVICE_URL.PROFILE}federation/${cboUuid}/bank`;
        const basicUrl = `${environment.fdmApiBaseUrl}${SERVICE_URL.PROFILE}federation/${cboUuid}/basic`;
        const ecMemberUrl = `${environment.fdmApiBaseUrl}${SERVICE_URL.PROFILE}federation/${cboUuid}/ec-members`;

        //  Single API call
        if (singleCall) {
            if (contentType === 'bank') {
                return this.http.get(bankUrl, { params: params });
            }

            if (contentType === 'basic') {
                return this.http.get(basicUrl, { params: basicParams });
            }
            if (contentType === 'member') {
                return this.http.get(ecMemberUrl, { params: params });
            }

            // Safety fallback
            return throwError(() => new Error('Invalid content type'));
        }

        //  Multiple API calls
        return forkJoin({
            bank: this.http.get(bankUrl, { params: params }),
            basic: this.http.get(basicUrl, { params: basicParams }),
        });
    }

    mapActiveBankAccounts(clfAccounts: any[], bankList: any[]): any[] {
        if (!bankList?.length) return [];

        const bankMap = new Map(
            bankList.map((bank: any) => [bank.bank_id, bank.bank_shortname])
        );

        return clfAccounts
            .filter(
                (acc: any) =>
                    acc.activation_status === 2 &&
                    acc.is_active === true &&
                    acc.status === 1 &&
                    bankMap.has(acc.bank_id)
            )
            .map((acc: any) => {
                const bankName = bankMap.get(acc.bank_id) || '';
                const accNo = acc.account_no ? String(acc.account_no) : '';
                const maskedAccNo = accNo
                    ? ' (' +
                      '*'.repeat(Math.max(accNo.length - 4, 0)) +
                      accNo.slice(-4) +
                      ')'
                    : '';
                return {
                    ...acc,
                    bank_name: bankName,
                    bank_shortname: bankName,
                    bank_display_name: bankName + maskedAccNo,
                };
            });
    }

    /**
     * Formats image URLs by normalizing legacy vault URLs to the new base URL.
     * @param imageName The original image URL or filename.
     * @param data Object containing the UUID for constructing fallback URLs.
     * @returns The formatted URL or an empty string if invalid.
     */
    formatImageUrl(
        imageName: string,
        data: { uuid: string },
        imageType?: ImageTypeEnum,
        shgId?: string
    ): string {
        if (!data?.uuid) {
            return '';
        }
        if (imageName && imageName.includes('http')) {
            return imageName;
        }

        // Basic UUID validation (optional, but recommended)
        const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(data.uuid)) {
            console.warn('Invalid UUID provided to formatImageUrl:', data.uuid);
            return '';
        }

        let returnUrl = '';

        if (imageType === ImageTypeEnum.Resolution) {
            returnUrl = `${this.imageBaseUrl}${data.uuid}/${imageName}`;
            // https://stage-fdm.lokos.in/api/vault/profile/v1/896b551b-6975-5a56-bc49-1387f0b159ac/resolution.jpg
        } else if (imageType === ImageTypeEnum.Bank) {
            returnUrl = `${this.imageBaseUrl}${data.uuid}/bank/${imageName}`;
            //https://stage-fdm.lokos.in/api/vault/profile/v1/vault-uat/6228d2ad-f80f-5e52-8c55-54d8e6542920/bank/223344556.jpg
        } else if (imageType === ImageTypeEnum.Profile) {
            returnUrl = `${this.imageBaseUrl}${shgId}/member/${data.uuid}/${imageName}`;
            //https://stage-fdm.lokos.in/api/vault/profile/v1/6aed35ac-cb67-5475-9efb-ca2f10e2cb70/member/378b7db4-017e-5d82-ae63-d18541427b18/profile.jpg
        } else if (imageType === ImageTypeEnum.MemberBank) {
            returnUrl = `${this.imageBaseUrl}${shgId}/member/${data.uuid}/bank/${imageName}`;
            //https://stage-fdm.lokos.in/api/vault/profile/v1/6aed35ac-cb67-5475-9efb-ca2f10e2cb70/member/378b7db4-017e-5d82-ae63-d18541427b18/bank/223344556.jpg
        }
        // try {
        //     const url = new URL(imageName);
        //     if (
        //         url.hostname === 'apis.lokos.in' &&
        //         (url.pathname.startsWith('/vault/') ||
        //             url.pathname.startsWith('/vault-uat/'))
        //     ) {
        //         return imageName
        //             .replace(environment.vaultBaseUrls[0], newBaseUrl)
        //             .replace(environment.vaultBaseUrls[1], newBaseUrl);
        //     } else if (
        //         url.hostname === 'api.lokos.in' &&
        //         !url.pathname.includes('/vault/')
        //     ) {
        //         return imageName.replace(environment.apiBaseUrl, newBaseUrl);
        //     }
        // } catch (error) {
        //     console.warn(
        //         'Invalid URL provided to formatImageUrl:',
        //         imageName,
        //         error
        //     );
        // }

        // Assuming imageName is a filename
        if (returnUrl) {
            return returnUrl;
        } else {
            return `${this.imageBaseUrl}${shgId}${data.uuid}/member/${data.uuid}/${imageName}`;
        }
    }

    formatImageNewUrl(imageName: string, data: any): string {
        const originalUrl = imageName;
        let fixedUrl: any = '';
        if (originalUrl.includes('vault') && originalUrl.includes('https')) {
            fixedUrl = originalUrl.replace(
                'https://apis.lokos.in/vault/',
                this.imageBaseUrl
            );
        } else if (
            !originalUrl.includes('vault') &&
            originalUrl.includes('https')
        ) {
            fixedUrl = originalUrl.replace(
                'https://api.lokos.in/',
                this.imageBaseUrl
            );
        } else {
            fixedUrl =
                this.imageBaseUrl +
                data.cbo_uuid +
                '/member/' +
                data.uuid +
                '/' +
                imageName;
        }
        return fixedUrl;
    }

    sortByName(array: any[], nameField: string, idField: string): any[] {
        return array.sort(
            (a, b) =>
                a[nameField].localeCompare(b[nameField]) ||
                a[idField] - b[idField]
        );
    }

    private static GEO_FIELD_MAP: Record<
        string,
        { nameField: string; enField: string }
    > = {
        state: { nameField: 'state_name', enField: 'state_name_en' },
        district: { nameField: 'district_name', enField: 'district_name_en' },
        block: { nameField: 'block_name', enField: 'block_name_en' },
        panchayat: {
            nameField: 'panchayat_name',
            enField: 'panchayat_name_en',
        },
        village: { nameField: 'village_name', enField: 'village_name_en' },
    };

    /**
     * Normalize geo list (set localized name w/ EN fallback) and sort alphabetically.
     * type: 'state' | 'district' | 'block' | 'panchayat' | 'village'
     */
    sortGeoList(list: any[], type: string): any[] {
        const cfg = CommonService.GEO_FIELD_MAP[type];
        if (!cfg || !Array.isArray(list)) return list || [];
        return this.sortByName(
            list.map((item: any) => ({
                ...item,
                [cfg.nameField]: item[cfg.enField] || item[cfg.nameField],
            })),
            cfg.nameField,
            cfg.enField
        );
    }

    /**
     * Wipe field.dropdownlist for given field names in a fields[] array.
     * Used to cascade-reset child geo dropdowns when parent changes.
     */
    clearFieldDropdowns(fields: any[], names: string[]) {
        if (!Array.isArray(fields)) return;
        names.forEach((name) => {
            const field = fields.find((f) => f && f.name === name);
            if (field) field.dropdownlist = [];
        });
    }

    sortByKey(arr: any[], key: string) {
        return arr.sort((a, b) => {
            if (a[key] < b[key]) return -1;
            if (a[key] > b[key]) return 1;
            return 0;
        });
    }

    quarterFilterData(showCurrQuarter = false, isAnnual = false) {
        return [
            { label: 'Q1 (Apr-Jun)', value: 'Q1' },
            { label: 'Q2 (Jul-sep)', value: 'Q2' },
            { label: 'Q3 (Oct-Dec)', value: 'Q3' },
            ...(showCurrQuarter
                ? [{ label: 'Q4 (Jan-Mar)', value: 'Q4' }]
                : []),
            ...(isAnnual ? [{ label: 'Annual (Apr-Mar)', value: 'ann' }] : []),
        ];
    }

    getQuarter(quarter?: string | null): [number, number] {
        if (quarter === 'Q1') {
            return [4, 6];
        } else if (quarter === 'Q2') {
            return [7, 9];
        } else if (quarter === 'Q3') {
            return [10, 12];
        } else if (quarter === 'Q4') {
            return [1, 3];
        } else {
            return [4, 3];
        }
    }

    maskValue(value: string | number, visibleLastDigits = 3): string {
        if (value === null || value === undefined) return '';
        const str = value.toString();
        if (str.length <= visibleLastDigits) {
            return str;
        }
        const maskedPart = '*'.repeat(str.length - visibleLastDigits);
        const visiblePart = str.slice(-visibleLastDigits);

        return maskedPart + visiblePart;
    }

    getFinancialYearsFilters(startYear = 2024, currentFyear = false) {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // Jan = 1

        // Financial year changes in April
        const currentFYStart =
            currentMonth >= 4 ? currentYear : currentYear - 1;

        const years = [];
        for (
            let year = currentFyear ? currentFYStart : currentFYStart - 1;
            year >= startYear;
            year--
        ) {
            years.push({
                label: `FY ${year} - ${(year + 1).toString().slice(-2)}`,
                value: `${year}-${year + 1}`,
            });
        }

        return years;
    }

    getFinancialYear() {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth();
        return month >= 3 ? year : year - 1;
    }

    monthToQuarter(month: number) {
        if (month < 3) {
            return 'Q4';
        } else if (month < 6) {
            return 'Q1';
        } else if (month < 9) {
            return 'Q2';
        } else {
            return 'Q3';
        }
    }

    monthlyQuarterFilterData() {
        return [
            { value: '01', fy: 0, quarter: 3, label: 'January (Q3)' },
            { value: '02', fy: 0, quarter: 3, label: 'February (Q3)' },
            { value: '03', fy: 0, quarter: 3, label: 'March (Q3)' },
            { value: '04', fy: 0, quarter: 4, label: 'April (Q4)' },
            { value: '05', fy: 0, quarter: 4, label: 'May (Q4)' },
            { value: '06', fy: 0, quarter: 4, label: 'June (Q4)' },
            { value: '07', fy: 1, quarter: 1, label: 'July (Q1)' },
            { value: '08', fy: 1, quarter: 1, label: 'August (Q1)' },
            { value: '09', fy: 1, quarter: 1, label: 'September (Q1)' },
            { value: '10', fy: 1, quarter: 2, label: 'October (Q2)' },
            { value: '11', fy: 1, quarter: 2, label: 'November (Q2)' },
            { value: '12', fy: 1, quarter: 2, label: 'December (Q2)' },
        ];
    }

    setDefaultPeriod() {
        const today = new Date();
        return formatDate(today, 'MMMM-yyyy', 'en-US');
    }

    groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
        return array.reduce(
            (result, item) => {
                const groupKey = String(item[key] ?? '');
                if (!result[groupKey]) result[groupKey] = [];
                result[groupKey].push(item);
                return result;
            },
            {} as Record<string, T[]>
        );
    }
}
